if (!customElements.get("product-info")) {
  class ProductInfo extends HTMLElement {
    abortController = undefined;
    onVariantChangeUnsubscriber = undefined;
    pendingRequestUrl = null;
    preProcessHtmlCallbacks = [];
    postProcessHtmlCallbacks = [];
    lastVariantGroupMapping = null;

    constructor() {
      super();

      this.onWindowLoad = this.onWindowLoad.bind(this);
    }

    get productId() {
      return this.getAttribute('data-product-id');
    }

    get sectionId() {
      return this.dataset.originalSection || this.dataset.sectionId;
    }

    get disableSelectedVariantDefault() {
      return this.dataset.disableSelectedVariantDefault === "true" || false;
    }

    get enableVariantGroupImages() {
      return this.dataset.enableVariantGroupImages === "true" || false;
    }

    get enableHistoryState() {
      return this.dataset.enableHistoryState === "true" || false;
    }

    get viewMode() {
      return this.dataset.viewMode || 'main-product'
    }

    get pickupAvailability() {
      return this.querySelector(`pickup-availability`);
    }

    get productForm() {
      return this.querySelector('product-form'); // fallback: product-form.m-product-form--main
    }

    get productMedia() {
      return this.querySelector(`[id^="MediaGallery-${this.dataset.sectionId}"]`);
    }

    get variantPicker() {
      return this.querySelector('variant-picker');
    }

    get quantityInput() {
      return this.querySelector(`m-quantity-input input`);
    }
    get quantityInputStickyAtc() {
      return this.querySelector(`.m-sticky-addtocart m-quantity-input input`);
    }

    get stickyAtc() {
      return this.querySelector('sticky-atc');
    }

    connectedCallback() {
      if (this.viewMode === "main-product") {
        window.addEventListener("load", this.onWindowLoad);
      }

      this.init();
    }

    disconnectedCallback() {
      window.removeEventListener("load", this.onWindowLoad);
      this.onVariantChangeUnsubscriber();
      this.cartUpdateUnsubscriber?.();
    }

    init() {
      this.currentVariant = this.getSelectedVariant(this);
      this.quantityInputs = [];

      if (this.quantityInput) {
        this.quantityInputs.push(this.quantityInput);
      }
      if (this.quantityInputStickyAtc) {
        this.quantityInputs.push(this.quantityInputStickyAtc);
      }

      if (this.disableSelectedVariantDefault) {
        this.handleDisableSelectedVariantDefault();
      }

      this.onVariantChangeUnsubscriber = MinimogEvents.subscribe(
        MinimogTheme.pubSubEvents.optionValueSelectionChange,
        this.handleOptionValueChange.bind(this)
      );

      this.showFeaturedMedia = this.dataset.showFeaturedMedia === "true" || false;

      this.initialMedias = this.productMedia ? this.productMedia.querySelectorAll('.m-product-media--item:not(.swiper-slide-duplicate)') : null;
      this.initialThumbs = this.productMedia ? this.productMedia.querySelectorAll(".m-product-media--slider__thumbnails [data-media-type]") : null;

      if (this.enableVariantGroupImages) {
        this.variantGroupImagesData = this.getVariantGroupImagesData();
        this.showFeaturedMedia = false;
      };

      this.initQuantityHandlers();

      if (this.currentVariant && !this.showFeaturedMedia) {
        this.updateMedia(this.currentVariant);
      }
    }

    getVariantGroupImagesData() {
      return (
        JSON.parse(
          this.querySelector('#variantGroup[type="application/json"]')
            .textContent
        ) || {}
      );
    }

    handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
      if (!this.contains(event.target)) return;

      const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
      const shouldSwapProduct = this.dataset.url !== productUrl;
      // this.pendingRequestUrl = productUrl;
      const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;
      const cachedVariant = shouldSwapProduct ? null : this.getCachedSelectedVariant();

      this.updateVariantInputs(cachedVariant?.id);
      this.toggleAddButton(true);
      this.stickyAtc?.toggleAddButton(true);

      if (cachedVariant) {
        this.updateCachedPrice(cachedVariant);
        this.updateMedia(cachedVariant);
      }

      this.renderProductInfo({
        requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
        targetId: target.id,
        callback: shouldSwapProduct
          ? this.handleSwapProduct(productUrl, shouldFetchFullPage, this.viewMode)
          : this.handleUpdateProductInfo(productUrl, this.viewMode),
      });
    }

    getCachedSelectedVariant() {
      const variants = window._themeProducts?.[this.productId]?.variants;
      const optionGroups = this.variantPicker?.querySelectorAll('.m-product-option');
      if (!Array.isArray(variants) || !optionGroups?.length) return null;

      const selectedOptions = Array.from(optionGroups).map((group) => {
        const select = group.querySelector('select');
        if (select) return select.value;
        return group.querySelector('input[type="radio"]:checked')?.value;
      });

      if (selectedOptions.some((value) => !value)) return null;
      return variants.find((variant) => selectedOptions.every((value, index) => variant.options?.[index] === value)) || null;
    }

    updateCachedPrice(variant) {
      const priceContainer = this.querySelector(`#Price-${this.dataset.sectionId}`);
      const priceWrapper = priceContainer?.querySelector('.m-price');
      if (!priceWrapper) return;

      const finalPriceActive = Boolean(variant.final_price_active);
      const productDisplayPrice = Number(finalPriceActive ? variant.final_price : variant.price) || 0;
      const eligible = variant.product_discount_eligible ?? (priceWrapper.dataset.productDiscountEligible !== 'false');
      priceWrapper.dataset.productDiscountEligible = String(eligible);
      const campaignLabel = priceWrapper.querySelector('[data-product-discount-label]');
      if (campaignLabel) campaignLabel.textContent = variant.clearance ? 'CLEARANCE' : priceWrapper.dataset.campaignLabel;
      const displayPrice = window.NelpandaFinalPrice?.applyOrderDiscount(productDisplayPrice, undefined, eligible) ?? productDisplayPrice;
      const compareAtPrice = Number(finalPriceActive ? variant.final_compare_at_price : variant.compare_at_price) || 0;
      const displayCompareAtPrice = compareAtPrice > productDisplayPrice ? compareAtPrice : productDisplayPrice;
      const onSale = displayCompareAtPrice > displayPrice;
      const moneyFormat = window.MinimogSettings?.money_format;

      priceWrapper.classList.toggle('m-price--on-sale', onSale);
      priceWrapper.classList.toggle('m-price--sold-out', !variant.available);
      priceWrapper.dataset.finalPriceActive = String(finalPriceActive);
      priceWrapper.dataset.productPrice = String(productDisplayPrice);
      priceWrapper.dataset.referencePrice = String(displayCompareAtPrice);

      priceWrapper.querySelectorAll('.m-price__regular .m-price-item--regular, .m-price__sale .m-price-item--sale')
        .forEach((node) => {
          node.textContent = formatMoney(displayPrice, moneyFormat);
        });
      priceWrapper.querySelectorAll('.m-price__sale .m-price-item--regular')
        .forEach((node) => {
          node.textContent = formatMoney(displayCompareAtPrice, moneyFormat);
        });

      const discountPercent = priceWrapper.querySelector('[data-discount-breakdown-percent]');
      if (discountPercent && onSale) {
        discountPercent.textContent = String(Math.round(((displayCompareAtPrice - productDisplayPrice) * 100) / displayCompareAtPrice));
      }
      window.NelpandaFinalPrice?.refresh(priceWrapper);
    }

    renderProductInfo({ requestUrl, targetId, callback }) {
      this.abortController?.abort();
      this.abortController = new AbortController();

      fetch(requestUrl, { signal: this.abortController.signal })
        .then((response) => response.text())
        .then((responseText) => {
          this.pendingRequestUrl = null;
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          callback(html);
        })
        .then(() => {
          // set focus to last clicked option value
          document.querySelector(`#${targetId}`)?.focus();
        })
        .catch((error) => {
          if (error.name === 'AbortError') {
            console.log('Fetch aborted by user');
          } else {
            console.error(error);
          }
        });
    }

    buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
      const params = [];

      !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

      if (optionValues.length) {
        params.push(`option_values=${optionValues.join(',')}`);
      }

      return `${url}?${params.join('&')}`;
    }

    handleUpdateProductInfo(productUrl, viewMode) {
      return (html) => {
        const quickView = html.querySelector('#MainProduct-quick-view__content');
        if (quickView && viewMode === 'quick-view') {
          html = new DOMParser().parseFromString(quickView.innerHTML, "text/html");
        }

        const variant = this.getSelectedVariant(html);

        this.pickupAvailability?.update(variant);
        this.updateOptionValues(html);
        this.updateURL(productUrl, variant?.id);
        this.updateVariantInputs(variant?.id);

        if (!variant) {
          this.setUnavailable();
          return;
        }

        this.updateMedia(variant);

        const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
          const source = html.getElementById(`${id}-${this.sectionId}`);
          const destination = this.querySelector(`#${id}-${this.dataset.sectionId}`);
          if (source && destination) {
            destination.innerHTML = source.innerHTML;
            destination.classList.toggle('m:hidden', shouldHide(source));
          }
        };

        updateSourceFromDestination('Price');
        updateSourceFromDestination("Inventory");
        updateSourceFromDestination("Sku");
        updateSourceFromDestination("Availability");
        updateSourceFromDestination("Volume");
        updateSourceFromDestination('PricePerItem');

        this.updateQuantityRules(this.sectionId, this.productId, html);
        this.querySelector(`#QuantityRules-${this.dataset.section}`)?.classList.remove('m:hidden');
        this.querySelector(`#VolumeNote-${this.dataset.section}`)?.classList.remove('m:hidden');

        const addButtonUpdated = html.getElementById(`ProductSubmitButton-${this.sectionId}`);
        this.toggleAddButton(
          addButtonUpdated ? addButtonUpdated.hasAttribute('disabled') : true,
          window.MinimogStrings.soldOut
        );

        this.currentVariant = variant;

        MinimogEvents.emit(MinimogTheme.pubSubEvents.variantChange, {
          data: { variant, sectionId: this.sectionId, html },
        });

        document.dispatchEvent(
          new CustomEvent('variant:changed', {
            detail: {
              variant: this.currentVariant,
            },
          })
        );
      };
    }

    handleSwapProduct(productUrl, updateFullPage, viewMode) {
      return (html) => {
        const quickView = html.querySelector('#MainProduct-quick-view__content');
        if (quickView && viewMode === 'quick-view') {
          html = new DOMParser().parseFromString(quickView.innerHTML, "text/html");
        }

        const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
        const productInfo = html.querySelector(selector);
        const variant = this.getSelectedVariant(productInfo);
        productInfo.dataset.disableSelectedVariantDefault = false;

        this.updateURL(productUrl, variant?.id);

        if (updateFullPage) {
          document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;
          HTMLUpdateUtility.viewTransition(
            document.querySelector('main'),
            html.querySelector('main'),
            this.preProcessHtmlCallbacks,
            this.postProcessHtmlCallbacks
          );
        } else {
          HTMLUpdateUtility.viewTransition(
            this,
            productInfo,
            this.preProcessHtmlCallbacks,
            this.postProcessHtmlCallbacks
          );
        }

        this.currentVariant = variant;
      };
    }

    getSelectedVariant(productInfoNode) {
      const selectedVariant = productInfoNode.querySelector('variant-picker [data-selected-variant]')?.innerHTML;
      return !!selectedVariant ? JSON.parse(selectedVariant) : null;
    }

    updateURL(url, variantId) {
      if (this.dataset.updateUrl === 'false') return;
      window.history.replaceState({}, '', `${url}${this.enableHistoryState && variantId ? `?variant=${variantId}` : ''}`);
    }

    updateVariantInputs(variantId) {
      document
        .querySelectorAll(`#product-form-${this.dataset.sectionId}, #product-form-installment-${this.dataset.sectionId}, #sticky-atc-form-${this.dataset.sectionId}`)
        .forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          input.value = variantId ?? '';
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
    }

    updateOptionValues(html) {
      const variantPicker = html.querySelector('variant-picker');
      if (variantPicker) {
        HTMLUpdateUtility.viewTransition(this.variantPicker, variantPicker, this.preProcessHtmlCallbacks);
      }
    }

    setUnavailable() {
      const productForm = document.getElementById(
        `product-form-${this.sectionId}`
      );
      const addButton = productForm.querySelector('[name="add"]');

      if (addButton) {
        this.toggleAddButton(true, window.MinimogStrings.unavailable);
      }

      const selectors = [
        "Price",
        "Inventory",
        "Sku",
        "Availability"
      ]
        .map((id) => `#${id}-${this.sectionId}`)
        .join(", ");
      document
        .querySelectorAll(selectors)
        .forEach((selector) => selector.classList.add('m:hidden'));
    }

    toggleAddButton(disable = true, text, modifyClass = true) {
      const productForm = document.getElementById(`product-form-${this.dataset.sectionId}`);
      if (!productForm) return;
      const addButton = productForm.querySelector('[name="add"]');
      const addButtonText = productForm.querySelector('[name="add"] > span.m-add-to-cart--text');
      if (!addButton) return;

      if (disable) {
        addButton.setAttribute('disabled', 'disabled');
        if (text) addButtonText.textContent = text;
      } else {
        addButton.removeAttribute('disabled');
        addButtonText.innerHTML = window.MinimogStrings.addToCart;
      }

      if (!modifyClass) return;
    }

    updateMedia(variant) {
      if (!this.productMedia) return;
      const variantGroupMapping = this.enableVariantGroupImages && this.variantGroupImagesData.enable
        ? this.variantGroupImagesData.mapping?.find((item) => Number(item.id) === variant.id)
        : null;

      if (variantGroupMapping) {
        if (this.lastVariantGroupMapping === variantGroupMapping) return;
        this.lastVariantGroupMapping = null;

        const slider = this.productMedia.slider;
        if (slider?.params?.loop) slider.loopDestroy();

        this.handleVariantGroupImage(variantGroupMapping);

        if (slider) {
          slider.updateSlides();
          if (slider.params.loop) {
            slider.loopCreate();
            slider.updateSlides();
          }
          this.productMedia.navSlider.updateSlides();

          slider.slideToLoop(0, 0, false);
          this.productMedia.navSlider.slideTo(0);
        }
        if (this.productMedia.enableImageZoom) {
          this.productMedia.handlePhotoswipe();
        }
        this.lastVariantGroupMapping = variantGroupMapping;
      } else {
        this.lastVariantGroupMapping = null;

        const setActiveMedia = () => {
          if (typeof this.productMedia.setActiveMedia === 'function') {
            this.productMedia.init();
            this.productMedia.setActiveMedia(variant);
            return true; // Indicate success
          }
          return false; // Indicate failure
        };

        if (!setActiveMedia()) {
          this.timer = setInterval(() => {
            if (setActiveMedia()) {
              clearInterval(this.timer);
            }
          }, 100);
        }
      }
    }

    handleDisableSelectedVariantDefault() {
      let urlParams = new URLSearchParams(window.location.search);
      if (urlParams.has("variant")) return;

      this.pickerFields = this.querySelectorAll("[data-picker-field]");

      this.pickerFields && this.pickerFields.forEach((field) => {
        let pickerType = field.dataset.pickerField;
        switch (pickerType) {
          case "select":
            let selectBox = field.querySelector("select");
            let option = document.createElement("option");
            option.text = this.dataset.variantOptionNoneText;
            option.setAttribute("disabled", "");
            option.setAttribute("selected", "");

            selectBox.add(option, 0);
            break;
          default:
            let checkedInputs = field.querySelectorAll("input:checked");
            checkedInputs &&
              checkedInputs.forEach(function (input) {
                input.removeAttribute("checked");
              });
            break;
        }

        field.dataset.selectedValue = "";
        if (field.querySelector(".option-label--selected")) field.querySelector(".option-label--selected").textContent = "";
      });

      this.setUnavailable();
      this.currentVariant = null;
    }

    getVariantMediaIds(selectedVariantData) {
      const featuredMediaId = selectedVariantData.featured_media ? String(selectedVariantData.featured_media) : null;
      const exclusiveMediaIds = new Set(
        [selectedVariantData.featured_media, ...(selectedVariantData.exclusive_media || [])]
          .filter(Boolean)
          .map(String)
      );
      const sharedMediaIds = new Set((this.variantGroupImagesData.shared_media || []).map(String));
      const orderedMediaIds = Array.from(this.initialMedias)
        .filter((media) => {
          const mediaId = media.dataset.mediaId;
          return media.dataset.mediaType !== 'image' || exclusiveMediaIds.has(mediaId) || sharedMediaIds.has(mediaId);
        })
        .map((media) => media.dataset.mediaId)
        .filter((mediaId) => mediaId !== featuredMediaId);

      return Array.from(new Set([featuredMediaId, ...orderedMediaIds].filter(Boolean)));
    }

    handleVariantGroupImage(selectedVariantData) {
      const selectedMediaIds = this.getVariantMediaIds(selectedVariantData);
      const selectedMedias = (initialItems) => {
        const selectedVariantMedias = Array.from(initialItems)
          .map((media) => {
            if (selectedMediaIds.includes(media.dataset.mediaId)) {
              return media;
            }
            return null;
          })
          .filter(Boolean);

        if (selectedVariantMedias.length <= 0) {
          return Array.from(initialItems).map((media, index) => {
            media.dataset.index = index;

            media.removeAttribute('style'); // Avoid style of swipe slide item
            media.querySelector('.m-product-media').setAttribute("style", `--animation-order: ${index + 1};`);

            return media;
          });
        }

        const orderedMedias = selectedMediaIds
          .map(id => selectedVariantMedias.find(media => media.dataset.mediaId === id))
          .filter(Boolean);

        return orderedMedias.map((media, index) => {
          media.dataset.index = index;

          media.removeAttribute('style'); // Avoid style of swipe slide item
          media.querySelector('.m-product-media').setAttribute("style", `--animation-order: ${index + 1};`);

          return media;
        });
      }

      const mediaWrapper = this.productMedia.querySelector(".m-media-gallery__list");
      const thumbWrapper = this.productMedia.querySelector(".m-product-media--slider__thumbnails .swiper-wrapper");

      const layout = this.productMedia.dataset.layout;
      const mediaItems = selectedMedias(this.initialMedias);
      const thumbItems = selectedMedias(this.initialThumbs);

      [...mediaItems, ...thumbItems].forEach((media) => {
        const image = media?.querySelector("responsive-image img");
        if (image) Object.assign(image.style, { animation: "none", opacity: "1", transform: "none", transition: "none" });
      });

      mediaItems.forEach((media, index) => {
        if (layout === "layout-2") {
          media.classList.toggle("m-col-span-2", index % 3 === 0);
        }
      });
      thumbItems.forEach((media) => {
        media.classList.toggle("swiper-slide-thumb-active", media.dataset.index === "0");
      });

      const sameSecondaryMedia = Array.from(mediaWrapper.children)
        .slice(1)
        .every((media, index) => media === mediaItems[index + 1]);
      const sameSecondaryThumbs = Array.from(thumbWrapper.children)
        .slice(1)
        .every((media, index) => media === thumbItems[index + 1]);

      if (
        mediaWrapper.children.length === mediaItems.length
        && thumbWrapper.children.length === thumbItems.length
        && sameSecondaryMedia
        && sameSecondaryThumbs
      ) {
        if (mediaWrapper.firstElementChild !== mediaItems[0]) {
          mediaWrapper.replaceChild(mediaItems[0], mediaWrapper.firstElementChild);
        }
        if (thumbWrapper.firstElementChild !== thumbItems[0]) {
          thumbWrapper.replaceChild(thumbItems[0], thumbWrapper.firstElementChild);
        }
        return;
      }

      mediaWrapper.innerHTML = "";
      thumbWrapper.innerHTML = "";

      mediaItems.forEach((media) => {
        mediaWrapper.append(media);
      });
      thumbItems.forEach((media) => {
        thumbWrapper.append(media);
      });
    }

    initQuantityHandlers() {
      if (this.quantityInputs.length <= 0) return;

      this.setQuantityBoundries();
      if (!this.hasAttribute("data-original-section-id")) {
        this.cartUpdateUnsubscriber = MinimogEvents.subscribe(
          MinimogTheme.pubSubEvents.cartUpdate,
          this.fetchQuantityRules.bind(this)
        );
      }
    }

    setQuantityBoundries() {
      this.quantityInputs.forEach((input) => {
        const quantityInputWrapper = input.closest('m-quantity-input');
        quantityInputWrapper.setQuantityBoundries(this.sectionId, this.productId);
      });
    }

    fetchQuantityRules() {
      const currentVariantId = this.productForm?.variantIdInput?.value;
      if (!currentVariantId) return;

      this.querySelector('.quantity__rules-cart')?.classList.add('loading');

      fetch(
        `${this.getAttribute(
          "data-url"
        )}?variant=${currentVariantId}&section_id=${this.sectionId}`
      )
        .then((response) => response.text())
        .then((responseText) => {
          const parsedHTML = new DOMParser().parseFromString(
            responseText,
            "text/html"
          );
          this.updateQuantityRules(
            this.sectionId,
            this.productId,
            parsedHTML
          );
        })
        .catch((error) => {
          console.error(error);
        })
        .finally(() => {
          this.querySelector('.quantity__rules-cart')?.classList.remove('loading');
        });
    }

    updateQuantityRules(sectionId, productId, parsedHTML) {
      if (this.quantityInputs.length <= 0) return;

      this.quantityInputs.forEach((input) => {
        const quantityInputWrapper = input.closest('m-quantity-input');
        quantityInputWrapper.updateQuantityRules(sectionId, productId, parsedHTML);
      });

      this.setQuantityBoundries();
    }

    addRecentViewedProduct() {
      const cookies = getCookie('m-recent-viewed-products')
      let products = cookies ? JSON.parse(cookies) : []
      if (products.indexOf(MinimogSettings.productHandle) === -1) {
        products.unshift(MinimogSettings.productHandle)
        products = products.slice(0, 20)
        setCookie('m-recent-viewed-products', JSON.stringify(products));
      }
    }

    onWindowLoad() {
      this.acc = [];
      MinimogTheme.CompareProduct && MinimogTheme.CompareProduct.setCompareButtonsState();
      MinimogTheme.Wishlist && MinimogTheme.Wishlist.setWishlistButtonsState();
      this.addRecentViewedProduct();

      addEventDelegate({
        context: this,
        selector: (window.__minimog_review_selector || '') + '.m-product-collapsible .jdgm-widget-actions-wrapper, .m-product-collapsible .spr-summary-actions-newreview',
        handler: (e) => {
          const index = e.target.closest('.m-product-collapsible').dataset.index
          setTimeout(() => {
            this.acc[Number(index)].setContentHeight()
          }, 300)
        },
        capture: true
      })
    }
  }

  customElements.define("product-info", ProductInfo);
}
